import { LeftOutlined, SyncOutlined, UserOutlined } from "@ant-design/icons";
import { Badge, Button, Flex, Spin } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRole, WorkStatus } from "tm-shared/types";

import { PwaInstall } from "@/components/Header/components/PwaInstall";
import { UploadIndicator } from "@/components/Header/components/UploadIndicator";
import {
    StyledHeader,
    StyledNotification,
    StyledRightActionsContainer,
    StyledTopContent,
} from "@/components/Header/Styled";
import { DataConfirmModal } from "@/components/Modals/DataConfirmModal";
import { PrimaryText } from "@/components/Typography/Styled";
import { ENVIRONMENT } from "@/config";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { usePWA } from "@/hooks/usePWA";
import { setNetworkStatus } from "@/store/slices/auth/auth.slice";
import {
    setShowOnlinePopup,
    updateOfflineStatus,
} from "@/store/slices/offline/offline.slice";

import { NetworkIndicator } from "../NetworkIndicator";
import MidnightCountdown from "./components/MidnightCountdown";

const canGoBack = () => {
    const idx = window.history.state?.idx;

    if (typeof idx === "number") return idx > 0;

    return window.history.length > 1;
};

interface HeaderProps {
    path?: string;
    title?: string | React.ReactNode;
    titleLoading?: boolean;
    back?: boolean;
    needConfirm?: boolean;
    rightActions?: React.ReactNode;
    titleFontSize?: number;
    locationState?: { state: unknown };
    withMidnightCountdown?: boolean;
    confirmText?: string;
}

const NETWORK_STATUS = {
    ONLINE: "online",
    OFFLINE: "offline",
    OFFLINE_WITHOUT_TEXT: "offline_without_text",
} as const;

const rolesWithStartDay = new Set<UserRole>([UserRole.Md, UserRole.Ta]);

const Header = ({
    path,
    title,
    back,
    rightActions,
    needConfirm,
    titleFontSize,
    locationState,
    withMidnightCountdown = false,
    titleLoading = false,
    confirmText,
}: HeaderProps) => {
    const navigate = useNavigate();
    const isPWA = usePWA();
    const { isOnline } = useAppSelector((state) => state.auth);
    const { offlineMode, showOnlinePopup } = useAppSelector(
        (state) => state.offline,
    );
    const { workStatus } = useAppSelector((state) => state.work);
    const { role } = useAppSelector((state) => state.user);
    const [isModalOpened, setIsModalOpened] = useState(false);

    const dispatch = useAppDispatch();
    const isFirstRender = useRef(true);

    const isUserOnline = workStatus === WorkStatus.StartWork;

    const handleApproveMode = useCallback(() => {
        dispatch(updateOfflineStatus(false));
        dispatch(setShowOnlinePopup(false));
    }, [dispatch]);

    const handleBackNavigation = useCallback(() => {
        if (needConfirm) {
            setIsModalOpened(true);
            return;
        }

        if (back && canGoBack()) {
            navigate(-1);
            return;
        }

        if (back) {
            navigate(ROUTES.Profile, { replace: true });
            return;
        }

        navigate(path, locationState);
    }, [needConfirm, back, navigate, path, locationState]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (isOnline) {
            dispatch(setNetworkStatus(NETWORK_STATUS.ONLINE));
            const timer = setTimeout(() => {
                dispatch(setNetworkStatus(null));
            }, 5000);
            return () => clearTimeout(timer);
        }

        if (isOnline === false) {
            dispatch(setNetworkStatus(NETWORK_STATUS.OFFLINE));
            const timer = setTimeout(() => {
                dispatch(setNetworkStatus(NETWORK_STATUS.OFFLINE_WITHOUT_TEXT));
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dispatch, isOnline]);

    const ProfileButton = () => {
        const hasStartDay = rolesWithStartDay.has(role);

        const icon = hasStartDay ? (
            <Badge
                dot
                status={isUserOnline ? "success" : "error"}
                offset={[-14, 18]}
            >
                <UserOutlined />
            </Badge>
        ) : (
            <UserOutlined />
        );

        return (
            <Button
                type="text"
                onClick={() => navigate(ROUTES.Profile)}
                icon={icon}
            />
        );
    };

    return (
        <>
            <StyledHeader $offline={offlineMode} $environment={ENVIRONMENT}>
                {withMidnightCountdown && <MidnightCountdown />}

                <Flex vertical style={{ width: "100%" }}>
                    <StyledTopContent>
                        <Flex gap={8} align="center">
                            {path || back ? (
                                <Button
                                    type="text"
                                    onClick={handleBackNavigation}
                                    icon={<LeftOutlined />}
                                />
                            ) : (
                                <ProfileButton />
                            )}

                            <NetworkIndicator canChangeOfflineMode />

                            <UploadIndicator />
                        </Flex>

                        <StyledRightActionsContainer>
                            {rightActions}

                            <PwaInstall isPWA={isPWA} />
                        </StyledRightActionsContainer>
                    </StyledTopContent>

                    {(title || titleLoading) && (
                        <Flex
                            gap="8px"
                            align="center"
                            justify="center"
                            style={{ paddingBottom: 10, paddingInline: 16 }}
                        >
                            <PrimaryText
                                fontSize={titleFontSize ?? 12}
                                lineHeight="100%"
                                textAlign="center"
                            >
                                {title || "Синхронізація"}
                            </PrimaryText>

                            {titleLoading && (
                                <Spin
                                    indicator={
                                        <SyncOutlined
                                            spin
                                            style={{ fontSize: 14 }}
                                        />
                                    }
                                />
                            )}
                        </Flex>
                    )}
                </Flex>

                {showOnlinePopup && (
                    <StyledNotification>
                        <PrimaryText fontSize={16}>
                            Додаток перейшов в онлайн режим. Підтвердьте
                            перехід.
                        </PrimaryText>

                        <Button onClick={handleApproveMode}>Підтвердити</Button>
                    </StyledNotification>
                )}
            </StyledHeader>

            {needConfirm && isModalOpened && (
                <DataConfirmModal
                    onClickBack={() => (back ? navigate(-1) : navigate(path))}
                    onClose={() => setIsModalOpened(false)}
                    infoText={confirmText}
                />
            )}
        </>
    );
};

export default Header;
